import { useTimedAnimation } from '@frontend/react';
import { CardContainer, Icon, Text, useViewportSize, XStack, YStack } from '@frontend/web-react';
import { applyClass, concatClasses } from '@frontend/web-utils';
import { GradientBorderCard } from '@frontend/web/components';
import { useEffect } from 'react';
import Confetti from 'react-confetti';

export const DEFAULT_TOTAL_STEPS = 5;

type OnboardingTaskCardProps = {
  ageOfUserInDays?: number;
  completedCount: number;
  totalSteps?: number;
};

export const OnboardingTaskCard = ({
  ageOfUserInDays = 0,
  completedCount,
  totalSteps = DEFAULT_TOTAL_STEPS,
}: OnboardingTaskCardProps) => {
  const { isAnimationOn, turnOnAnimation } = useTimedAnimation({ duration: 5000 });
  const { height, width } = useViewportSize();
  const clampedCount = Math.min(Math.max(completedCount, 0), totalSteps);
  const canAnimate = hasOnboardingTasksCardAnimation();
  const completedSteps = clampedCount >= totalSteps;

  useEffect(() => {
    if (!canAnimate) {
      return;
    }

    if (completedSteps) {
      turnOnAnimation();
      window.addEventListener('beforeunload', clearHasOnboardingTasksCardAnimation);
    }
    return () => {
      if (completedSteps) {
        clearHasOnboardingTasksCardAnimation();
        window.removeEventListener('beforeunload', clearHasOnboardingTasksCardAnimation);
      }
    };
  }, [clampedCount, totalSteps, canAnimate]);

  if (!canAnimate) {
    if (completedSteps) {
      return null;
    }
    setHasOnboardingTasksCardAnimation();
  }

  const cardContent = (
    <CardContainer className={concatClasses('p-lg', applyClass(isAnimationOn, 'border-none', 'border-borderTertiary'))}>
      <YStack gapY="md">
        <YStack gapY="xs">
          <Text type="h5">Onboarding goal</Text>
          <Text color="secondary" type="p-sm">
            {ageOfUserInDays >= 30
              ? `Offload ${totalSteps} things in your first month`
              : `Offload ${totalSteps} things`}
          </Text>
        </YStack>
        <XStack alignItems="center" gapX="md">
          {Array.from({ length: totalSteps }, (_, index) =>
            index < clampedCount ? (
              <CompletedCircle key={index} />
            ) : (
              <IncompleteCircle key={index} stepNumber={index + 1} />
            ),
          )}
        </XStack>
        <Text color="secondary" type="p-sm">
          {getEncouragementText(clampedCount, totalSteps)}
        </Text>
      </YStack>
    </CardContainer>
  );

  return (
    <>
      {isAnimationOn ? <GradientBorderCard>{cardContent}</GradientBorderCard> : cardContent}
      {isAnimationOn ? <Confetti className="absolute" height={height} style={{ zIndex: 1000 }} width={width} /> : null}
    </>
  );
};

export const getEncouragementText = (completedCount: number, totalSteps: number): string => {
  const remaining = totalSteps - completedCount;

  if (remaining <= 0) {
    return "You did it! You've completed your onboarding goal.";
  }

  if (completedCount === 0) {
    return 'Submit your first task to get started.';
  }

  if (completedCount === 1) {
    return 'One thing off your plate. Offload more to build the habit.';
  }

  if (remaining === 1) {
    return 'Almost done! Just one more task.';
  }

  return `You're making progress! ${remaining} more to go.`;
};

const ONBOARDING_TASKS_CARD = 'onboardingTasksCard';

const hasOnboardingTasksCardAnimation = () => sessionStorage.getItem(ONBOARDING_TASKS_CARD) === 'true';

const setHasOnboardingTasksCardAnimation = () => sessionStorage.setItem(ONBOARDING_TASKS_CARD, 'true');

const clearHasOnboardingTasksCardAnimation = () => sessionStorage.removeItem(ONBOARDING_TASKS_CARD);

const STEP_CIRCLE_SIZE = 48;

const CompletedCircle = () => (
  <div
    className="flex items-center justify-center rounded-full bg-surfaceAccent"
    style={{ height: STEP_CIRCLE_SIZE, width: STEP_CIRCLE_SIZE }}
  >
    <Icon color="primary" name="IconCheck" size="md" />
  </div>
);

const IncompleteCircle = ({ stepNumber }: { stepNumber: number }) => (
  <div
    className="flex items-center justify-center rounded-full border-2 border-dashed border-neutral-300"
    style={{ height: STEP_CIRCLE_SIZE, width: STEP_CIRCLE_SIZE }}
  >
    <Text className="text-neutral-400" type="p-sm">
      {stepNumber}
    </Text>
  </div>
);
